import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as SignalR from '@aspnet/signalr';
import { Store } from '@ngxs/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvService } from '../../models/env.service';
import { PushNotificationSettings } from '../models/signalr-query-result.interface';
import { GraphQLClientService } from './graphql-client.service';
import { LoginService } from './login-service';
import { MonitoringService } from '../../services/logging.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly negotiationRetryInSeconds = 1; // each second.

  private hubConnection: SignalR.HubConnection;
  private channels = {};
  private subscriptions: [() => void] = [() => { }];

  private graphQLClient: GraphQLClientService;

  private isHubClosed: boolean = false;

  constructor(
    private store: Store,
    private router: Router,
    private oAuthService: OAuthService,
    private env: EnvService,
    private loginService: LoginService,
    private monitoringService: MonitoringService,
  ) {
    this.graphQLClient = new GraphQLClientService(
      oAuthService,
      router,
      env.environment.notificationUrl,
      env
    );
  }

  public async connectToNotifications(
    saveSettingsInStore: (settings: PushNotificationSettings) => void,
    getSttings: () => PushNotificationSettings
  ) {
    try {
      const settings = getSttings();
      if (!settings?.negotiations) {
        await this.negotiate(saveSettingsInStore, getSttings);
      }
      this.hubConnection = new SignalR.HubConnectionBuilder()
        .withUrl(getSttings().negotiations.url, {
          accessTokenFactory: () => this.negotiate(saveSettingsInStore, getSttings),
        })
        .configureLogging(SignalR.LogLevel.Error)
        .build();
      await this.hubConnection.start();
    } catch (e) {
      this.monitoringService.logException(e);
      await this.connectToNotifications(saveSettingsInStore, getSttings);
    }
  }

  public channel<TPayload>(channelName: string): Observable<TPayload> {
    try {
      if (!Object.keys(this.channels).find((x) => x === channelName)) {
        const subject = new BehaviorSubject<TPayload>(null);
        this.channels[channelName] = subject;
        const subscription = () => {
          this.hubConnection.on(channelName, (payload) => {
            subject.next(JSON.parse(payload));
          });
          this.hubConnection.onclose(() => {
            if (!this.isHubClosed) {
              console.log('Hub connection was restarted');
              this.hubConnection.start();
              setTimeout(() => {
                this.isHubClosed = false;
              }, 500);
            }
            this.isHubClosed = true;
          });
        };

        this.subscriptions.push(subscription);
        subscription();

        return subject.asObservable();
      }

      return this.channels[channelName];
    } catch (e) {
      this.monitoringService.logException(e);
      console.log('Caught. Hub connection restarting');
      this.hubConnection.start();
    }

  }

  private async negotiate(
    saveSettingsInStore: (settings: PushNotificationSettings) => void,
    getSttings: () => PushNotificationSettings
  ) {
    this.graphQLClient.setHeader('operatorId', '1');
    const query = `query{negotiations{url accessToken channels}}`;
    return await this.graphQLClient
      .requestAsync<PushNotificationSettings>(query)
      .then(({ data }) => {
        saveSettingsInStore(data);
        return data.negotiations.accessToken;
      })
      .catch(async () => {
        await this.sleep(this.negotiationRetryInSeconds * 1000);
        const settings = getSttings();
        if (!settings?.negotiations) {
          return this.negotiate(saveSettingsInStore, getSttings);
        } else {
          await this.sleep(this.negotiationRetryInSeconds * 1000);
          return this.negotiate(saveSettingsInStore, getSttings);
        }
      });
  }

  private async sleep(msec) {
    return await new Promise((resolve) => setTimeout(resolve, msec));
  }
}
